import { cast, detach, flow, toGenerator, types } from "mobx-state-tree";
import { OperationFailure, OperationInProgress, OperationNotStarted, OperationStatus, OperationStatusModel, OperationSuccess } from "models/common/OperationStatus";
import { isKnownStatus, ProfileUpdateRequestModel } from "models/ota/ProfileUpdateRequestModel";
import oversight from "services/oversight";
import { getDeploymentId } from "./rootStore";

export const ProfileUpdateRequestsStore = types.model('ProfileUpdateRequestsStore', {
  // Active requests, and ended ones that were not dismissed yet, of the deployment's vehicles
  requests: types.array(ProfileUpdateRequestModel),
  fetchStatus: types.optional(OperationStatusModel, () => OperationNotStarted()),
  createStatus: types.optional(OperationStatusModel, () => OperationNotStarted()),
  // map of request id to its dismiss status
  dismissStatuses: types.map(OperationStatusModel),
})
.views(self => ({
  get isLoading() {
    return self.fetchStatus.inProgress
  },
  get isAllDismissible() {
    return self.requests.length > 0 && self.requests.every(request => request.isDismissible)
  },
  isDismissing(requestId: number) {
    return self.dismissStatuses.get(requestId.toString())?.inProgress ?? false
  },
  // The vehicle's request that is not ended yet, if any
  getActiveRequest(vehicleId: number) {
    return self.requests.find(request => request.vehicle_id === vehicleId && request.isActive)
  },
}))
.actions(self => {
  // Detaching the previous status keeps it accessible to whoever still holds it (see VehiclesStore)
  const _updateDismissStatus = (requestId: number, status: OperationStatus) => {
    const oldStatus = self.dismissStatuses.get(requestId.toString())
    if (oldStatus !== undefined) {
      detach(oldStatus)
    }
    self.dismissStatuses.set(requestId.toString(), status)
  }

  const _dismissRequest = flow(function* _dismissRequest(requestId: number) {
    _updateDismissStatus(requestId, OperationInProgress())
    const response = yield* toGenerator(oversight.dismissProfileUpdateRequest(requestId))
    _updateDismissStatus(requestId, response.error ? OperationFailure(response.error) : OperationSuccess())
  })

  const getLiveOrPendingRequests = flow(function* getLiveOrPendingRequests() {
    self.fetchStatus = OperationInProgress()
    const response = yield* toGenerator(oversight.getLiveOrPendingProfileUpdateRequests(getDeploymentId()))

    if (response.error) {
      self.fetchStatus = OperationFailure(response.error)
      return
    }

    // Invalid data would otherwise leave the fetch in progress, and stop the polling
    try {
      // Requests with a status added in oversight and not known here yet are ignored
      self.requests = cast(response.result.filter(request => isKnownStatus(request.status)))
    } catch (error) {
      self.fetchStatus = OperationFailure(error instanceof Error ? error : new Error(String(error)))
      return
    }
    self.fetchStatus = OperationSuccess()
  })

  return {
    getLiveOrPendingRequests,
    // Returns the id of the created request, or null on failure
    create: flow(function* create(vehicleId: number, profileId: string) {
      self.createStatus = OperationInProgress()
      const response = yield* toGenerator(oversight.createProfileUpdateRequest({ vehicle_id: vehicleId, profile_id: profileId }))

      if (response.error) {
        self.createStatus = OperationFailure(response.error)
        return null
      }

      self.createStatus = OperationSuccess()
      return response.result.id
    }),
    // Requests the same profile for several vehicles at once, with one status for all of them.
    // Returns true when all the requests were created.
    createForVehicles: flow(function* createForVehicles(vehicleIds: number[], profileId: string) {
      self.createStatus = OperationInProgress()
      const responses = yield* toGenerator(Promise.all(vehicleIds.map(vehicleId =>
        oversight.createProfileUpdateRequest({ vehicle_id: vehicleId, profile_id: profileId })
      )))
      void getLiveOrPendingRequests()

      const errors = responses.flatMap(response => response.error ? [response.error] : [])
      const firstError = errors[0]
      if (firstError !== undefined) {
        const message = vehicleIds.length > 1
          ? `${errors.length} of ${vehicleIds.length} requests failed. ${firstError.message}`
          : firstError.message
        self.createStatus = OperationFailure({ name: firstError.name, message })
        return false
      }

      self.createStatus = OperationSuccess()
      return true
    }),
    dismiss: flow(function* dismiss(requestId: number) {
      yield _dismissRequest(requestId)
      void getLiveOrPendingRequests()
    }),
    // Oversight has no dismiss-all, so each of the requests is dismissed on its own
    dismissAll: flow(function* dismissAll() {
      yield Promise.all(self.requests.map(request => _dismissRequest(request.id)))
      void getLiveOrPendingRequests()
    }),
    reset() {
      self.requests.clear()
      self.dismissStatuses.clear()
      self.fetchStatus = OperationNotStarted()
      self.createStatus = OperationNotStarted()
    },
  }
})
